import { getApiClient } from '@src/shared/types/api-client'
import { QueueMessage, QueueRefreshError, QueueSnapshot, RequeueAllDto, RequeueMessagesDto } from '@src/tools/dead-letter/types/QueueTypes'
import { MutationFunction, QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const SCOPE = 'queues'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  summary: () => [{ scope: SCOPE, entity: 'summary' }] as const,
  refreshErrors: () => [{ scope: SCOPE, entity: 'refreshErrors' }] as const,
  messages: (storageAccountName: string, queueName: string) =>
    [{ scope: SCOPE, entity: 'messages', storageAccountName, queueName }] as const,
}

function updateSnapshotCount(queryClient: QueryClient, storageAccountName: string, queueName: string, messageCount: number) {
  queryClient.setQueryData<QueueSnapshot[]>(keysFactory.summary(), (old) =>
    old?.map((s) =>
      s.storageAccountName === storageAccountName && s.queueName === queueName
        ? { ...s, messageCount }
        : s,
    ),
  )
}

const getQueueSummary = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.get('Queues/Summary')
  return response.data as QueueSnapshot[]
}

export function useQueueSummary(): [QueueSnapshot[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.summary()],
    queryFn: getQueueSummary,
    placeholderData: [],
  })

  return [data ?? [], isFetching]
}

const getQueueRefreshErrors = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.get('Queues/RefreshErrors')
  return response.data as QueueRefreshError[]
}

export function useQueueRefreshErrors(): [QueueRefreshError[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.refreshErrors()],
    queryFn: getQueueRefreshErrors,
    placeholderData: [],
  })

  return [data ?? [], isFetching]
}

const getQueueMessages = async (storageAccountName: string, queueName: string) => {
  const apiClient = getApiClient()
  const response = await apiClient.get(`Queues/${storageAccountName}/${queueName}/Messages`)
  return response.data as QueueMessage[]
}

export function useQueueMessages(storageAccountName: string, queueName: string, enabled = true): [QueueMessage[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.messages(storageAccountName, queueName)],
    queryFn: () => getQueueMessages(storageAccountName, queueName),
    placeholderData: [],
    enabled,
  })

  return [data ?? [], isFetching]
}

interface QueueActionResult { requeuedCount: number; messageCount: number; messages: QueueMessage[] }

const requeueMessages: MutationFunction<QueueActionResult, RequeueMessagesDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('Queues/Requeue', dto)
  return response.data as QueueActionResult
}

export function useRequeueMessages(): [
  MutationFunction<QueueActionResult, RequeueMessagesDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: requeueMessages,
    onSuccess: (data, variables) => {
      updateSnapshotCount(queryClient, variables.storageAccountName, variables.queueName, data.messageCount)
      queryClient.setQueryData<QueueMessage[]>(keysFactory.messages(variables.storageAccountName, variables.queueName), data.messages)
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

const requeueAll: MutationFunction<QueueActionResult, RequeueAllDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('Queues/RequeueAll', dto)
  return response.data as QueueActionResult
}

export function useRequeueAll(): [
  MutationFunction<QueueActionResult, RequeueAllDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: requeueAll,
    onSuccess: (data, variables) => {
      updateSnapshotCount(queryClient, variables.storageAccountName, variables.queueName, data.messageCount)
      queryClient.setQueryData<QueueMessage[]>(keysFactory.messages(variables.storageAccountName, variables.queueName), data.messages)
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

interface DeleteActionResult { deletedCount: number; messageCount: number; messages: QueueMessage[] }

const deleteMessages: MutationFunction<DeleteActionResult, RequeueMessagesDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('Queues/Delete', dto)
  return response.data as DeleteActionResult
}

export function useDeleteMessages(): [
  MutationFunction<DeleteActionResult, RequeueMessagesDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: deleteMessages,
    onSuccess: (data, variables) => {
      updateSnapshotCount(queryClient, variables.storageAccountName, variables.queueName, data.messageCount)
      queryClient.setQueryData<QueueMessage[]>(keysFactory.messages(variables.storageAccountName, variables.queueName), data.messages)
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

interface ClearQueueResult { cleared: boolean; messageCount: number; messages: QueueMessage[] }

const clearQueue: MutationFunction<ClearQueueResult, RequeueAllDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('Queues/Clear', dto)
  return response.data as ClearQueueResult
}

export function useClearQueue(): [
  MutationFunction<ClearQueueResult, RequeueAllDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: clearQueue,
    onSuccess: (data, variables) => {
      updateSnapshotCount(queryClient, variables.storageAccountName, variables.queueName, data.messageCount)
      queryClient.setQueryData<QueueMessage[]>(keysFactory.messages(variables.storageAccountName, variables.queueName), data.messages)
    },
  })

  return [mutateAsync, isPending, isError, reset]
}
